import { useEffect, useState } from 'react';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../../../Config/firebase';
import { motion } from 'framer-motion';
import { Tooltip, CircularProgress, Box, Typography } from '@mui/material';
import { useAppSelector } from '../../../../state/state';
import { useLocation } from 'react-router-dom';
import { selectEffectiveUID } from '../../../../state/UserStatesV2/userSliceV2';

interface TodayStats {
    paidOrders: number;
    paidAmount: number;
    settledOrders: number;
    settledAmount: number;
    platformFees: number;
    transferAmount: number;
}

interface OrderDetails {
    id: string;
    subscriptionName: string;
    consumerName: string;
    amount: number;
    time: Timestamp;
    type: 'paid' | 'settled';
    orderType: string;
    settlementId: string | null;
    isSettled: boolean;
    platformFees: number;
    transferAmount: number;
    settlementTime: Timestamp;
}

interface GroupedSettlement {
    settlementId: string;
    orders: OrderDetails[];
    totalAmount: number;
    totalPlatformFees: number;
    totalTransferAmount: number;
    time: Timestamp;
}

type TimeFilter = 'today' | 'week';

export default function TodayTransactions() {
    const [isLoading, setIsLoading] = useState(true);
    const [timeFilter, setTimeFilter] = useState<TimeFilter>('today');
    const [stats, setStats] = useState<TodayStats>({
        paidOrders: 0,
        paidAmount: 0,
        settledOrders: 0,
        settledAmount: 0,
        platformFees: 0,
        transferAmount: 0
    });
    const [groupedSettlements, setGroupedSettlements] = useState<GroupedSettlement[]>([]);
    const [error, setError] = useState<string | null>(null);
    const ServiceProviderId = useAppSelector(selectEffectiveUID);
    const isUserLoading = useAppSelector((state) => state.userSliceV2.status === 'loading');

    // Get the current URL parameters
    const location = useLocation();

    useEffect(() => {
        // Read the filter from the URL
        const params = new URLSearchParams(location.search);
        const filter = params.get('filter');
        
        if (filter === 'today' || filter === 'week') {
            setTimeFilter(filter as TimeFilter);
        }
    }, [location.search]); // Update when the URL changes

    useEffect(() => {
        if (isUserLoading) {
            setIsLoading(true);
            return;
        }

        

        loadTransactions();
    }, [ServiceProviderId, timeFilter, isUserLoading]);

    const loadTransactions = async () => {
        try {
            setIsLoading(true);

            if (!ServiceProviderId) {
                throw new Error("ServiceProviderId is not defined");
            }

            const ordersRef = collection(db, 'ServiceProviders', ServiceProviderId, 'Orders');

            // Calculate start date based on filter
            const now = new Date();
            const startDate = new Date();
            
            if (timeFilter === 'today') {
                // Set to beginning of today
                startDate.setHours(0, 0, 0, 0);
            } else if (timeFilter === 'week') {
                // Set to beginning of the week (Sunday)
                const day = startDate.getDay(); // 0 is Sunday
                startDate.setDate(startDate.getDate() - day);
                startDate.setHours(0, 0, 0, 0);
            }

            const startTimestamp = Timestamp.fromDate(startDate);
            const nowTimestamp = Timestamp.fromDate(now);
            
 

            // Query to get paid orders
            const paidOrdersQuery = query(
                ordersRef,
                where('paymentTime', '>=', startTimestamp),
                where('paymentTime', '<=', nowTimestamp),
                where('payment_status', '==', 'Paid')
            );

            const paidOrdersSnapshot = await getDocs(paidOrdersQuery);

            // Query to get settled orders - this is key for showing settlements
            // We need to query for orders that were SETTLED in the time range,
            // not just orders that were PAID in the time range
            const settledOrdersQuery = query(
                ordersRef,
                where('settlementTime', '>=', startTimestamp),
                where('settlementTime', '<=', nowTimestamp),
                where('settlement_status', '==', 'Settled')
            );

            const settledOrdersSnapshot = await getDocs(settledOrdersQuery);

            // Combine both result sets, removing duplicates
            const allOrdersMap = new Map();
            
            // Add paid orders
            paidOrdersSnapshot.docs.forEach(doc => {
                allOrdersMap.set(doc.id, { doc, data: doc.data() });
            });
            
            // Add settled orders
            settledOrdersSnapshot.docs.forEach(doc => {
                allOrdersMap.set(doc.id, { doc, data: doc.data() });
            });

            // Convert map to array of orders
            const orders: OrderDetails[] = Array.from(allOrdersMap.values()).map(({ doc, data }) => {
                return {
                    id: doc.id,
                    subscriptionName: data.subscriptionName || 'Unknown',
                    consumerName: data.consumerName || 'Unknown',
                    amount: data.chargeable || 0,
                    time: data.paymentTime,
                    type: 'paid',
                    orderType: data.orderType || 'Unknown',
                    settlementId: data.settlementId || null,
                    settlementTime: data.settlementTime || null,
                    isSettled: data.settlement_status === 'Settled' || data.orderType === 'cash',
                    platformFees: data.platformFees || 0,
                    transferAmount: data.transferAmount || 0
                };
            });

            // Group orders by settlementId
            const settlementGroups: Record<string, OrderDetails[]> = {};
            const tempNonGroupedOrders: OrderDetails[] = [];

            orders.forEach(order => {
                // Cash orders are instantly settled and don't have a settlementId
                if (order.orderType === 'cash') {
                    order.settlementId = 'CASH'; // Assign a special settlementId for grouping
                    order.isSettled = true;
                    
                    // For cash orders, check if the payment time is within our time range
                    if (order.time >= startTimestamp && order.time <= nowTimestamp) {
                        tempNonGroupedOrders.push(order); // Add to non-grouped orders temporarily
                    }
                }
                // Orders with settlementId are grouped 
                else if (order.settlementId && order.settlementTime) {
                    // For settled orders, check if the settlement time is within our time range
                    if (order.settlementTime >= startTimestamp && order.settlementTime <= nowTimestamp) {
                        if (!settlementGroups[order.settlementId]) {
                            settlementGroups[order.settlementId] = [];
                        }
                        settlementGroups[order.settlementId].push(order);
                    }
                }
                // Orders without settlementId are not settled yet, show if payment was in range
                else if (order.time >= startTimestamp && order.time <= nowTimestamp) {
                    tempNonGroupedOrders.push(order); // Add to non-grouped orders temporarily
                }
            });

            // Group cash orders into a special settlement batch
            const cashOrders = tempNonGroupedOrders.filter(order => order.orderType === 'cash');
            if (cashOrders.length > 0) {
                settlementGroups['CASH'] = cashOrders;
            }

            // Group remaining unprocessed orders (pending settlements) into a special batch
            const pendingOrders = tempNonGroupedOrders.filter(order => order.orderType !== 'cash');
            if (pendingOrders.length > 0) {
                settlementGroups['PENDING'] = pendingOrders;
            }

            // Create grouped settlements
            const settlements: GroupedSettlement[] = Object.keys(settlementGroups).map(settlementId => {
                const groupOrders = settlementGroups[settlementId];
                const totalAmount = groupOrders.reduce((sum, order) => sum + order.amount, 0);
                const totalPlatformFees = groupOrders.reduce((sum, order) => sum + order.platformFees, 0);
                const totalTransferAmount = groupOrders.reduce((sum, order) => sum + order.transferAmount, 0);
                
                // Use the most recent time from the group
                // For cash orders or pending orders, use payment time as settlement time
                let latestTime;
                if (settlementId === 'CASH' || settlementId === 'PENDING') {
                    // Sort by time and use the latest one
                    const sortedOrders = [...groupOrders].sort((a, b) => b.time.seconds - a.time.seconds);
                    latestTime = sortedOrders[0].time;
                } else {
                    latestTime = groupOrders.reduce(
                        (latest, order) => order.settlementTime.seconds > latest.seconds ? order.settlementTime : latest,
                        groupOrders[0].settlementTime
                    );
                }
                
                return {
                    settlementId: settlementId === 'CASH' 
                        ? 'Cash Transactions'
                        : settlementId === 'PENDING'
                            ? 'Pending Settlements'
                            : settlementId,
                    orders: groupOrders,
                    totalAmount,
                    totalPlatformFees,
                    totalTransferAmount,
                    time: latestTime
                };
            });

            // Sort settlements by time (most recent first)
            settlements.sort((a, b) => b.time.seconds - a.time.seconds);
            
            // Calculate statistics
            const paidOrders = orders.filter(order => 
                order.time >= startTimestamp && 
                order.time <= nowTimestamp
            );

            const paidAmount = paidOrders.reduce((sum, order) => sum + order.amount, 0);

            // For settled stats, only count orders that were settled in the time period
            const settledOrdersList = orders.filter(order => 
                order.isSettled && 
                order.settlementTime &&
                order.settlementTime >= startTimestamp && 
                order.settlementTime <= nowTimestamp
            );

            const settledOrders = settledOrdersList.length;
            const settledAmount = settledOrdersList.reduce((sum, order) => sum + order.transferAmount, 0);
            
            // Calculate total platform fees and transfer amount (only for the filtered time period)
            const totalPlatformFees = paidOrders.reduce((sum, order) => sum + order.platformFees, 0);
            const totalTransferAmount = settledOrdersList.reduce((sum, order) => sum + order.transferAmount, 0);

            setStats({
                paidOrders: paidOrders.length,
                paidAmount,
                settledOrders,
                settledAmount,
                platformFees: totalPlatformFees,
                transferAmount: totalTransferAmount
            });

            setGroupedSettlements(settlements);
            setIsLoading(false);
        } catch (error) {
            console.error('Error loading transaction statistics:', error);
            setIsLoading(false);
        }
    };

    if (isUserLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!ServiceProviderId) {
        return <div>Loading user data...</div>;
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    const hasTransactions = groupedSettlements.length > 0;

    return (
        <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="mb-6 flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                        {timeFilter === 'today' ? "Today's Transactions" : "This Week's Transactions"}
                    </h2>
                    <p className="text-gray-600">
                        {timeFilter === 'today' 
                            ? "Overview of today's payments and settlements" 
                            : "Overview of this week's payments and settlements"}
                    </p>
                </div>
                
                {/* Time Filter Toggle */}
                <div className="flex rounded-md shadow-sm bg-gray-100">
                    <button
                        className={`px-4 py-2 text-sm font-medium transition-colors duration-200 rounded-l-md ${
                            timeFilter === 'today'
                                ? 'bg-cc_brand text-white'
                                : 'text-gray-700 hover:bg-gray-200'
                        }`}
                        onClick={() => setTimeFilter('today')}
                    >
                        Today
                    </button>
                    <button
                        className={`px-4 py-2 text-sm font-medium transition-colors duration-200 rounded-r-md ${
                            timeFilter === 'week'
                                ? 'bg-cc_brand text-white'
                                : 'text-gray-700 hover:bg-gray-200'
                        }`}
                        onClick={() => setTimeFilter('week')}
                    >
                        This Week
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* Payments Card */}
                <Tooltip title={`Orders paid ${timeFilter === 'today' ? 'today' : 'this week'}`} arrow placement="top">
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200 shadow-sm">
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900">
                                    {timeFilter === 'today' ? "Today's Payments" : "This Week's Payments"}
                                </h3>
                                <p className="text-3xl font-bold text-purple-600 mt-2">
                                    ₹{stats.paidAmount.toFixed(2)}
                                </p>
                                <p className="text-sm text-gray-600 mt-2">{stats.paidOrders} orders</p>
                            </div>
                            <motion.div
                                className="p-3 bg-purple-600 bg-opacity-10 rounded-full"
                                animate={{
                                    scale: [1, 1.1, 1],
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                }}
                            >
                                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </motion.div>
                        </div>
                    </div>
                </Tooltip>

                {/* Settlements Card */}
                <Tooltip title={`Orders settled ${timeFilter === 'today' ? 'today' : 'this week'}`} arrow placement="top">
                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-6 rounded-xl border border-indigo-200 shadow-sm">
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900">
                                    {timeFilter === 'today' ? "Today's Settlements" : "This Week's Settlements"}
                                </h3>
                                <p className="text-3xl font-bold text-indigo-600 mt-2">
                                    ₹{stats.settledAmount.toFixed(2)}
                                </p>
                                <p className="text-sm text-gray-600 mt-2">{stats.settledOrders} orders</p>
                            </div>
                            <motion.div
                                className="p-3 bg-indigo-600 bg-opacity-10 rounded-full"
                                animate={{
                                    scale: [1, 1.1, 1],
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                }}
                            >
                                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </motion.div>
                        </div>
                    </div>
                </Tooltip>

                {/* Platform Fees Card */}
                <Tooltip title={`Platform fees ${timeFilter === 'today' ? 'today' : 'this week'}`} arrow placement="top">
                    <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-xl border border-amber-200 shadow-sm">
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900">
                                    Platform Fees
                                </h3>
                                <p className="text-3xl font-bold text-amber-600 mt-2">
                                    ₹{stats.platformFees.toFixed(2)}
                                </p>
                                <p className="text-sm text-gray-600 mt-2">
                                    {stats.paidAmount > 0 
                                        ? ((stats.platformFees / stats.paidAmount) * 100).toFixed(1) + '% of payments'
                                        : '0% of payments'
                                    }
                                </p>
                            </div>
                            <motion.div
                                className="p-3 bg-amber-600 bg-opacity-10 rounded-full"
                                animate={{
                                    scale: [1, 1.1, 1],
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                }}
                            >
                                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                </svg>
                            </motion.div>
                        </div>
                    </div>
                </Tooltip>

               
            </div>

            {/* Transactions */}
            {hasTransactions ? (
                <div className="space-y-8">
                    {/* Grouped Settlements */}
                    {groupedSettlements.length > 0 && (
                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-4">All Transactions</h3>
                            <div className="space-y-4">
                                {groupedSettlements.map((settlement, index) => (
                                    <div key={index} className={`${
                                        settlement.settlementId === 'Cash Transactions' 
                                            ? 'bg-green-50 border-green-200' 
                                            : settlement.settlementId === 'Pending Settlements'
                                                ? 'bg-yellow-50 border-yellow-200'
                                                : 'bg-blue-50 border-blue-200'
                                        } rounded-lg p-4 border`}>
                                        <div className="flex justify-between items-center mb-2">
                                            <div>
                                                <span className={`font-medium ${
                                                    settlement.settlementId === 'Cash Transactions' 
                                                        ? 'text-green-800' 
                                                        : settlement.settlementId === 'Pending Settlements'
                                                            ? 'text-yellow-800'
                                                            : 'text-blue-800'
                                                }`}>
                                                    {settlement.settlementId}
                                                </span>
                                                <span className="ml-4 text-sm text-gray-600">
                                                    {settlement.time.toDate().toLocaleDateString()} {settlement.time.toDate().toLocaleTimeString()}
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-4">
                                                <div className={`${
                                                    settlement.settlementId === 'Cash Transactions' 
                                                        ? 'bg-green-100 text-green-800'
                                                        : settlement.settlementId === 'Pending Settlements'
                                                            ? 'bg-yellow-100 text-yellow-800' 
                                                            : 'bg-blue-100 text-blue-800'
                                                } px-3 py-1 rounded-full text-sm font-medium`}>
                                                    Total: ₹{settlement.totalAmount.toFixed(2)}
                                                </div>
                                                <div className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                                                    Fees: ₹{settlement.totalPlatformFees.toFixed(2)}
                                                </div>
                                                <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                                                    Transfer: ₹{settlement.totalTransferAmount.toFixed(2)}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-2">
                                            <table className={`min-w-full divide-y ${
                                                settlement.settlementId === 'Cash Transactions' 
                                                    ? 'divide-green-200'
                                                    : settlement.settlementId === 'Pending Settlements'
                                                        ? 'divide-yellow-200' 
                                                        : 'divide-blue-200'
                                            }`}>
                                                <thead className={`${
                                                    settlement.settlementId === 'Cash Transactions' 
                                                        ? 'bg-green-100'
                                                        : settlement.settlementId === 'Pending Settlements'
                                                            ? 'bg-yellow-100' 
                                                            : 'bg-blue-100'
                                                }`}>
                                                    <tr>
                                                        <th className={`px-4 py-2 text-center text-xs font-medium uppercase tracking-wider ${
                                                            settlement.settlementId === 'Cash Transactions' 
                                                                ? 'text-green-800'
                                                                : settlement.settlementId === 'Pending Settlements'
                                                                    ? 'text-yellow-800' 
                                                                    : 'text-blue-800'
                                                        }`}>
                                                            Time
                                                        </th>
                                                        <th className={`px-4 py-2 text-center text-xs font-medium uppercase tracking-wider ${
                                                            settlement.settlementId === 'Cash Transactions' 
                                                                ? 'text-green-800'
                                                                : settlement.settlementId === 'Pending Settlements'
                                                                    ? 'text-yellow-800' 
                                                                    : 'text-blue-800'
                                                        }`}>
                                                            Subscription
                                                        </th>
                                                        <th className={`px-4 py-2 text-center text-xs font-medium uppercase tracking-wider ${
                                                            settlement.settlementId === 'Cash Transactions' 
                                                                ? 'text-green-800'
                                                                : settlement.settlementId === 'Pending Settlements'
                                                                    ? 'text-yellow-800' 
                                                                    : 'text-blue-800'
                                                        }`}>
                                                            Customer
                                                        </th>
                                                        <th className={`px-4 py-2 text-center text-xs font-medium uppercase tracking-wider ${
                                                            settlement.settlementId === 'Cash Transactions' 
                                                                ? 'text-green-800'
                                                                : settlement.settlementId === 'Pending Settlements'
                                                                    ? 'text-yellow-800' 
                                                                    : 'text-blue-800'
                                                        }`}>
                                                            Type
                                                        </th>
                                                        <th className={`px-4 py-2 text-center text-xs font-medium uppercase tracking-wider ${
                                                            settlement.settlementId === 'Cash Transactions' 
                                                                ? 'text-green-800'
                                                                : settlement.settlementId === 'Pending Settlements'
                                                                    ? 'text-yellow-800' 
                                                                    : 'text-blue-800'
                                                        }`}>
                                                            Amount
                                                        </th>
                                                        <th className={`px-4 py-2 text-center text-xs font-medium uppercase tracking-wider ${
                                                            settlement.settlementId === 'Cash Transactions' 
                                                                ? 'text-green-800'
                                                                : settlement.settlementId === 'Pending Settlements'
                                                                    ? 'text-yellow-800' 
                                                                    : 'text-blue-800'
                                                        }`}>
                                                            Fees
                                                        </th>
                                                        <th className={`px-4 py-2 text-center text-xs font-medium uppercase tracking-wider ${
                                                            settlement.settlementId === 'Cash Transactions' 
                                                                ? 'text-green-800'
                                                                : settlement.settlementId === 'Pending Settlements'
                                                                    ? 'text-yellow-800' 
                                                                    : 'text-blue-800'
                                                        }`}>
                                                            Transfer
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className={`${
                                                    settlement.settlementId === 'Cash Transactions' 
                                                        ? 'bg-green-50'
                                                        : settlement.settlementId === 'Pending Settlements'
                                                            ? 'bg-yellow-50' 
                                                            : 'bg-blue-50'
                                                } divide-y ${
                                                    settlement.settlementId === 'Cash Transactions' 
                                                        ? 'divide-green-200'
                                                        : settlement.settlementId === 'Pending Settlements'
                                                            ? 'divide-yellow-200' 
                                                            : 'divide-blue-200'
                                                }`}>
                                                    {settlement.orders.map((order, orderIdx) => (
                                                        <tr key={orderIdx} className="hover:bg-gray-50">
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-center">
                                                                {order.time.toDate().toLocaleDateString()} {order.time.toDate().toLocaleTimeString()}
                                                            </td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-center">{order.subscriptionName}</td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-center">{order.consumerName}</td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-center">{order.orderType}</td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-center">₹{order.amount.toFixed(2)}</td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-center">₹{order.platformFees.toFixed(2)}</td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-center">₹{order.transferAmount.toFixed(2)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <p className="text-gray-500">
                        No transactions {timeFilter === 'today' ? 'today' : 'this week'}
                    </p>
                </div>
            )}
        </div>
    );
} 